import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Grid,
  Alert,
  Paper,
  Stack,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import DashboardCard from '../components/DashboardCard';
import { useAuthApp } from '../services/queries';
import { INTERNAL_APPS } from '../helper/constants';
import ModalSetUserPass from '../components/ModalSetUserPass';
import ModalVerifySuperAppsPassword from '../components/ModalVerifySuperAppsPassword';
import ModalUpdateAppPassword from '../components/ModalUpdateAppPassword';
import useAuth from '../hooks/useAuth';
import { useLoginApps, useLogin, useResetCredentialPassword } from '../services/mutations';
import toast from 'react-hot-toast';

const initForm = { application_code: '', username: '', password: '' };
const Dashboard = () => {
  const { isLoading, data, error, refetch } = useAuthApp();
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedChangePasswordCard, setSelectedChangePasswordCard] = useState(null);
  const [superAppsPassword, setSuperAppsPassword] = useState('');
  const [updateCredsPayload, setUpdateCredsPayload] = useState({ username: '', password: '' });
  const [isSuperAppsVerified, setIsSuperAppsVerified] = useState(false);
  const location = useLocation();
  const showCards = useMemo(
    () => location.pathname === '/',
    [location.pathname],
  );
  const {
    filteredApps,
    isLoading: isLoadingAuthMe,
    originUsername,
  } = useAuth();
  const loginApps = useLoginApps();
  const verifySuperAppsPassword = useLogin();
  const resetCredentialPassword = useResetCredentialPassword();
  const [credsPayload, setCredsPayload] = useState(initForm);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleSubmitLoginApp = async (e) => {
    e.preventDefault();
    const body = { ...credsPayload, application_code: selectedCard?.code };

    if (!credsPayload.username || !credsPayload.password) {
      toast.error('Username or password are required');
      return;
    }

    const url = selectedCard?.url?.split('=') || '';

    loginApps.mutate(
      { originUsername, body },
      {
        onSuccess: async (res) => {
          toast.success(res.data.message || 'Success add new credentials');

          // Update is_connected temporarily for matching code
          if (data?.data) {
            const targetApp = data.data.find(
              (app) => app.code === selectedCard?.code,
            );
            if (targetApp) {
              targetApp.is_connected = true;
            }
          }

          refetch();
          setSelectedCard(null);
        },
        onError: (err) => {
          const msg =
            err?.response?.data?.detail || 'Set username or password is failed';
          // setError(msg);
          toast.error(`Error, ${msg}`);
        },
      },
    );
  };

  const handleOpenChangePassword = (card) => {
    setSelectedChangePasswordCard(card);
    setSuperAppsPassword('');
    setUpdateCredsPayload({ username: '', password: '' });
    setIsSuperAppsVerified(false);
  };

  const handleCloseChangePassword = () => {
    setSelectedChangePasswordCard(null);
    setSuperAppsPassword('');
    setUpdateCredsPayload({ username: '', password: '' });
    setIsSuperAppsVerified(false);
  };

  const handleVerifySuperAppsPassword = () => {
    if (!superAppsPassword) {
      toast.error('Password SuperApps wajib diisi');
      return;
    }
    if (!originUsername) {
      toast.error('Username SuperApps tidak ditemukan');
      return;
    }

    verifySuperAppsPassword.mutate(
      { username: originUsername, password: superAppsPassword },
      {
        onSuccess: () => {
          setIsSuperAppsVerified(true);
          toast.success('Verifikasi password berhasil');
        },
        onError: (err) => {
          const msg = err?.response?.data?.detail || 'Verifikasi password gagal';
          toast.error(`Error, ${msg}`);
        },
      },
    );
  };

  const handleUpdateAppPassword = () => {
    if (!selectedChangePasswordCard?.code) {
      toast.error('Aplikasi belum dipilih');
      return;
    }
    if (!updateCredsPayload.username || !updateCredsPayload.password) {
      toast.error('Username dan password aplikasi wajib diisi');
      return;
    }

    const body = {
      application_code: selectedChangePasswordCard.code,
      username: updateCredsPayload.username,
      password: updateCredsPayload.password,
    };

    resetCredentialPassword.mutate(
      body,
      {
        onSuccess: async (res) => {
          toast.success(res?.message || 'Success update password');
          refetch();
          handleCloseChangePassword();
        },
        onError: (err) => {
          const msg = err?.response?.data?.detail || 'Update password gagal';
          toast.error(`Error, ${msg}`);
        },
      },
    );
  };

  const renderCards = useMemo(
    () =>
      data?.data && data.data.length > 0 ? (
        <Grid container spacing={{ xs: 1.5, sm: 2.5 }} justifyContent="flex-start">
          {data.data.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={index}>
              <DashboardCard
                setSelectedCard={setSelectedCard}
                card={card}
                onChangePassword={handleOpenChangePassword}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography variant="body1" sx={{ mt: 2 }}>
          Tidak ada daftar aplikasi tersedia
        </Typography>
      ),
    [data],
  );

  const internalApps = () => {
    return filteredApps.map((el, index) => (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={index}>
        <DashboardCard isInternal card={el} />
      </Grid>
    ));
  };

  const internalAppsMemo = useMemo(() => {
    return INTERNAL_APPS.map((el, index) => (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={index}>
        <DashboardCard isInternal card={el} />
      </Grid>
    ));
    // <Typography variant="body1" sx={{ mt: 2 }}>
    //   Tidak ada daftar aplikasi tersedia
    // </Typography>
  }, []);

  const stats = useMemo(() => {
    const externalApps = data?.data || [];
    const totalExternal = externalApps.length;
    const connected = externalApps.filter((app) => app.is_connected).length;
    const disconnected = totalExternal - connected;
    const internalCount = filteredApps?.length || 0;

    return [
      {
        label: 'Total External Apps',
        value: totalExternal,
        color: 'var(--text-primary)',
        badge: 'Apps',
      },
      {
        label: 'Connected Apps',
        value: connected,
        color: 'var(--primary-color)',
        badge: 'Active',
      },
      {
        label: 'Not Connected',
        value: disconnected,
        color: 'var(--danger-color)',
        badge: 'Pending',
      },
      {
        label: 'Internal Apps',
        value: internalCount,
        color: 'var(--warning-color)',
        badge: 'Internal',
      },
    ];
  }, [data?.data, filteredApps]);

  if (isLoadingAuthMe || isLoading)
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box className="page-shell">
      <Box className="page-header">
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            Dashboard
          </Typography>
          <Typography className="muted-text" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Ringkasan koneksi aplikasi internal dan eksternal
          </Typography>
        </Box>
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src="/tvri.jpg"
            alt="TVRI Logo"
            style={{ width: 80, objectFit: 'contain' }}
          />
        </Box>
      </Box>

      <Box className="stat-row mb-3">
        {stats.map((item) => (
          <Box key={item.label} className="stat-card">
            <Typography
              className="stat-label"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                color: item.color,
                fontWeight: 'bold',
                fontSize: { xs: 24, sm: 28 },
                my: 0.5
              }}
            >
              {item.value}
            </Typography>
            <Typography
              variant="body2"
              className="muted-text"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {item.badge}
            </Typography>
          </Box>
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error.message}
        </Alert>
      )}

      <Paper className="section-card" sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography variant="h6" fontWeight={'bold'} sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Aplikasi Super Apps
            </Typography>
            <Typography className="muted-text" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Akses langsung ke SuperApps
            </Typography>
          </Box>
        </Stack>
        <Grid container spacing={{ xs: 1.5, sm: 2.5 }} justifyContent="flex-start">
          {internalApps()}
        </Grid>
      </Paper>

      <Paper className="section-card" sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography variant="h6" fontWeight={'bold'} sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Aplikasi Pegawai
            </Typography>
            <Typography className="muted-text" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Kelola kredensial dan koneksi
            </Typography>
          </Box>
        </Stack>
        {showCards && renderCards}
      </Paper>

      <ModalSetUserPass
        open={selectedCard ? true : false}
        onClose={() => setSelectedCard(null)}
        onConfirm={handleSubmitLoginApp}
        setCredsPayload={setCredsPayload}
        credsPayload={credsPayload}
      />

      <ModalVerifySuperAppsPassword
        open={!!selectedChangePasswordCard && !isSuperAppsVerified}
        onClose={handleCloseChangePassword}
        onConfirm={handleVerifySuperAppsPassword}
        password={superAppsPassword}
        setPassword={setSuperAppsPassword}
        isLoading={verifySuperAppsPassword.isLoading}
      />

      <ModalUpdateAppPassword
        open={!!selectedChangePasswordCard && isSuperAppsVerified}
        onClose={handleCloseChangePassword}
        onConfirm={handleUpdateAppPassword}
        appName={selectedChangePasswordCard?.name}
        payload={updateCredsPayload}
        setPayload={setUpdateCredsPayload}
        isLoading={resetCredentialPassword.isLoading}
      />
    </Box>
  );
};

export default Dashboard;
